import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Upload, X } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { generatePTReport } from '@/services/ptPDFTemplate'
import { DEFAULT_PT_EQUIPMENT, PT_DEFAULT_TEST_SITE, PT_TEST_SITES } from '@/types/ptTypes'
import type { PTFormData } from '@/types/ptTypes'
import { generateOrderNumber, getNextMethodReportSequence } from '@/utils/getNextReportSequence'
import { getNdtExitPath } from '@/utils/ndtNavigation'
import { applySelectedEquipmentToForm, attachNdtReportToEquipment } from '@/utils/ndtEquipmentReport'
import { useNdtPersonnel } from '@/hooks/useNdtPersonnel'
import NdtFormPage, { NdtFormActions, NdtStatusButtons, ndtInputCls, ndtLabelCls, ndtSelectCls, ndtTextareaCls, ndtToggleCls, ndtUploadCls, ndtPrimaryBtnCls, ndtSecondaryBtnCls } from '@/components/ndt/NdtFormPage'
import NdtEquipmentSearch, { type NdtEquipmentHit } from '@/components/ndt/NdtEquipmentSearch'

const MATERIALS = [
  { value: 'Stainless steel', label: 'Rustfritt stål' },
  { value: 'Aluminium', label: 'Aluminium' },
] as const

const PTTestForm = () => {
  const navigate = useNavigate()
  const { inspectors, admins, companies, currentUser, loading, isAdmin, isInspectorLocked } = useNdtPersonnel()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null)

  const [formData, setFormData] = useState<PTFormData>({
    testDate: new Date().toISOString().split('T')[0],
    testSite: PT_DEFAULT_TEST_SITE,
    reportNumber: '',
    objects: [''],
    objectDescription: '',
    issuedBy: '',
    verifiedBy: '',
    status: 'Accepted',
    objectType: 'Component',
    equipmentSettings: { ...DEFAULT_PT_EQUIPMENT },
    extentOfExamination: '100',
    extentOfExaminationType: 'percentage',
    material: 'Stainless steel',
    comments: '',
    company: '',
    report_code: null,
    attachedImage: undefined,
  })

  useEffect(() => {
    if (isInspectorLocked && currentUser?.full_name && !formData.issuedBy) {
      setFormData((prev) => ({ ...prev, issuedBy: currentUser.full_name }))
    }
  }, [isInspectorLocked, currentUser, formData.issuedBy])

  const canSubmit = useMemo(() => {
    if (!formData.reportNumber.trim() || !formData.issuedBy.trim()) return false
    if (!formData.objectDescription.trim()) return false
    if (formData.objects.every((obj) => !obj.trim())) return false
    return true
  }, [formData])

  const selectedCompanyName = useMemo(
    () => companies.find((c) => c.id === formData.company)?.name ?? '',
    [companies, formData.company],
  )

  const handleSelectEquipment = (equipment: NdtEquipmentHit | null) => {
    setSelectedEquipmentId(equipment?.id ?? null)
    if (!equipment) return
    setFormData((prev) => applySelectedEquipmentToForm(prev, equipment))
  }

  const handleCompanyChange = (companyId: string) => {
    const selected = companies.find((c) => c.id === companyId)
    setSelectedEquipmentId(null)
    if (!selected) {
      setFormData((prev) => ({ ...prev, company: '', report_code: null }))
      return
    }
    setFormData((prev) => ({
      ...prev,
      company: companyId,
      report_code: selected.report_code,
      reportNumber: selected.report_code ? generateOrderNumber(selected.report_code) : prev.reportNumber,
    }))
  }

  const buildPayload = useCallback(
    (sequenceNumber: string) => ({
      ...formData,
      objects: formData.objects.filter((obj) => obj.trim()),
      certificationNumber: '---',
      sequenceNumber,
    }),
    [formData],
  )

  const handlePreview = async () => {
    setIsGeneratingPreview(true)
    try {
      const { sequenceNumber } = await getNextMethodReportSequence('PT', formData.reportNumber)
      const blob = await generatePTReport(buildPayload(sequenceNumber))
      setPreviewUrl(URL.createObjectURL(blob))
    } catch (error) {
      console.error(error)
      alert('Kunne ikke generere forhåndsvisning.')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!canSubmit) {
      alert('Vennligst fyll ut alle påkrevde felt.')
      return
    }
    setIsSubmitting(true)
    try {
      const { storageFolder, sequenceNumber, reportId } = await getNextMethodReportSequence('PT', formData.reportNumber)
      const folderPath = `${storageFolder}/${reportId}.pdf`
      const filteredObjects = formData.objects.filter((obj) => obj.trim())
      const pdfBlob = await generatePTReport(buildPayload(sequenceNumber))

      const { error: uploadError } = await supabase.storage
        .from('reports')
        .upload(folderPath, pdfBlob, { contentType: 'application/pdf', upsert: true })
      if (uploadError) throw uploadError

      const { data: { user } } = await supabase.auth.getUser()
      const { attachedImage: _omit, ...stored } = formData
      const insertData: Record<string, unknown> = {
        order_number: reportId,
        unit_id: filteredObjects.join(', ') || formData.objectDescription,
        test_date: formData.testDate,
        test_type: 'PT',
        status: formData.status,
        issued_by: formData.issuedBy,
        verified_by: formData.verifiedBy?.trim() || null,
        file_path: folderPath,
        report_code: formData.report_code,
        created_by: user?.id,
        report_data: { ...stored, objects: filteredObjects, sequenceNumber, equipmentId: selectedEquipmentId },
      }
      if (formData.company?.trim()) insertData.company_id = formData.company
      if (selectedEquipmentId) insertData.equipment_id = selectedEquipmentId

      const { error: metadataError } = await supabase.from('ndt_reports').insert(insertData)
      if (metadataError) throw metadataError

      if (selectedEquipmentId) {
        await attachNdtReportToEquipment({
          pdfBlob,
          fileName: `${reportId}.pdf`,
          customerName: selectedCompanyName,
          serialNumber: filteredObjects[0] || formData.objectDescription || reportId,
          equipmentId: selectedEquipmentId,
          testDate: formData.testDate,
          reportStatus: formData.status,
        })
      }

      const { data: signed } = await supabase.storage.from('reports').createSignedUrl(folderPath, 60)
      if (signed?.signedUrl) window.open(signed.signedUrl, '_blank')

      alert(selectedEquipmentId
        ? 'Rapport generert, lagret på utstyr og NDT-datoer oppdatert.'
        : 'Rapport generert og lagret.')
      navigate(getNdtExitPath())
    } catch (error) {
      console.error(error)
      alert(error instanceof Error ? error.message : 'Feil ved generering av rapport. Prøv igjen.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => setFormData((prev) => ({ ...prev, attachedImage: reader.result as string }))
    reader.readAsDataURL(file)
  }

  return (
    <NdtFormPage
      highlight="Penetrant"
      title="Testing"
      loading={loading}
      previewUrl={previewUrl}
      previewTitle="PT-rapport forhåndsvisning"
      onClosePreview={() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }}
      onPublish={() => void handleSubmit()}
      isPublishing={isSubmitting}
      canPublish={canSubmit}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Testdato</label>
            <input type="date" required value={formData.testDate} onChange={(e) => setFormData((p) => ({ ...p, testDate: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Teststed</label>
            <select value={formData.testSite} onChange={(e) => setFormData((p) => ({ ...p, testSite: e.target.value }))} className={ndtSelectCls}>
              {PT_TEST_SITES.map((site) => (
                <option key={site} value={site}>{site}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Kunde</label>
            <select value={formData.company || ''} onChange={(e) => handleCompanyChange(e.target.value)} className={ndtSelectCls}>
              <option value="">Velg kunde</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={ndtLabelCls}>Vårt ordrenummer</label>
            <input type="text" required value={formData.reportNumber} onChange={(e) => setFormData((p) => ({ ...p, reportNumber: e.target.value }))} className={ndtInputCls} />
          </div>
        </div>

        <NdtEquipmentSearch
          key={formData.company || 'none'}
          companyId={formData.company || ''}
          onSelect={handleSelectEquipment}
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Objektbeskrivelse</label>
            <input type="text" required value={formData.objectDescription} onChange={(e) => setFormData((p) => ({ ...p, objectDescription: e.target.value }))} className={ndtInputCls} />
            <div className="flex gap-2 mt-2">
              {MATERIALS.map((m) => {
                const selected = formData.material === m.value
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, material: m.value }))}
                    className={ndtToggleCls(selected)}
                  >
                    {m.label}
                  </button>
                )
              })}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              <label className="flex items-center cursor-pointer min-h-[40px]">
                <input
                  type="radio"
                  checked={formData.extentOfExaminationType === 'spec'}
                  onChange={() => setFormData((p) => ({ ...p, extentOfExaminationType: 'spec', extentOfExamination: 'Acc. to spec' }))}
                  className="mr-2 w-5 h-5 accent-[#1E3A5F]"
                />
                <span className="text-sm text-slate-700">Acc. to spec</span>
              </label>
              <div className="flex items-center gap-2 min-h-[40px]">
                <label className="flex items-center cursor-pointer shrink-0">
                  <input
                    type="radio"
                    checked={formData.extentOfExaminationType === 'percentage'}
                    onChange={() => setFormData((p) => ({
                      ...p,
                      extentOfExaminationType: 'percentage',
                      extentOfExamination: p.extentOfExamination === 'Acc. to spec' ? '100' : p.extentOfExamination,
                    }))}
                    className="mr-2 w-5 h-5 accent-[#1E3A5F]"
                  />
                  <span className="text-sm text-slate-700">Prosent</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.extentOfExaminationType === 'percentage' ? formData.extentOfExamination : ''}
                  onChange={(e) => setFormData((p) => ({
                    ...p,
                    extentOfExaminationType: 'percentage',
                    extentOfExamination: e.target.value,
                  }))}
                  className="w-16 min-h-[44px] px-2 text-sm text-center border border-slate-300 rounded-md focus:outline-none focus:border-[#1E3A5F] text-slate-800 bg-white placeholder:text-slate-400"
                  placeholder="%"
                />
              </div>
            </div>
          </div>
          <div>
            <label className={ndtLabelCls}>Objekt-ID</label>
            {formData.objects.map((obj, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input type="text" value={obj} onChange={(e) => {
                  const objects = [...formData.objects]
                  objects[index] = e.target.value
                  setFormData((p) => ({ ...p, objects }))
                }} className={`${ndtInputCls} flex-1`} placeholder={`Objekt ${index + 1}`} />
                {formData.objects.length > 1 && (
                  <button type="button" onClick={() => setFormData((p) => ({ ...p, objects: p.objects.filter((_, i) => i !== index) }))} className="p-2 text-red-600 hover:bg-red-50 rounded-md">
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setFormData((p) => ({ ...p, objects: [...p.objects, ''] }))} className="inline-flex items-center gap-2 text-sm text-[#0D9488] font-medium min-h-[44px]">
              <Plus size={18} /> Legg til objekt
            </button>
          </div>
        </div>

        {isAdmin && (
          <div>
            <label className={ndtLabelCls}>Utført av</label>
            <select required value={formData.issuedBy} onChange={(e) => setFormData((p) => ({ ...p, issuedBy: e.target.value }))} className={ndtSelectCls}>
              <option value="">Velg inspektør</option>
              {inspectors.map((i) => <option key={i.id} value={i.full_name}>{i.full_name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className={ndtLabelCls}>Ordreansvarlig</label>
          <select value={formData.verifiedBy || ''} onChange={(e) => setFormData((p) => ({ ...p, verifiedBy: e.target.value }))} className={ndtSelectCls}>
            <option value="">Ingen</option>
            {admins.map((a) => <option key={a.id} value={a.full_name}>{a.full_name}</option>)}
          </select>
        </div>

        <NdtStatusButtons
          value={formData.status}
          onChange={(status) => setFormData((p) => ({ ...p, status }))}
        />

        {formData.status === 'Not Accepted' && (
          <div>
            <label className={ndtLabelCls}>Kommentarer</label>
            <textarea rows={4} value={formData.comments || ''} onChange={(e) => setFormData((p) => ({ ...p, comments: e.target.value }))} className={ndtTextareaCls} />
          </div>
        )}

        <div>
          <label className={ndtLabelCls}>Bilde (valgfritt)</label>
          {!formData.attachedImage ? (
            <label className={ndtUploadCls}>
              <Upload size={16} className="text-slate-400" />
              <span className="text-sm text-slate-600">Last opp bilde</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          ) : (
            <div className="relative">
              <img src={formData.attachedImage} alt="Vedlegg" className="w-full h-64 object-cover rounded-md border border-slate-200" />
              <button type="button" onClick={() => setFormData((p) => ({ ...p, attachedImage: undefined }))} className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full">
                <X size={20} />
              </button>
            </div>
          )}
        </div>

        <NdtFormActions>
          <button type="button" onClick={() => void handlePreview()} disabled={isSubmitting || isGeneratingPreview} className={ndtSecondaryBtnCls}>
            {isGeneratingPreview ? 'Genererer…' : 'Forhåndsvis'}
          </button>
          <button type="submit" disabled={isSubmitting || !canSubmit} className={ndtPrimaryBtnCls}>
            {isSubmitting ? 'Genererer rapport...' : 'Generer PT-rapport'}
          </button>
        </NdtFormActions>
      </form>
    </NdtFormPage>
  )
}

export default PTTestForm
